import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'cookies-js';
import { useStatsig } from '../hooks/useStatsig';
import RouteLoading from '../RouteLoading';

// A stale or rejected `jwt` never resolves to a user, so the wait is capped
// rather than unbounded — an unbounded one would blank the page forever.
export const IDENTITY_WAIT_MS = 2000;

const StatsigRouteSwitch = ({ gate, enabledTo, Component, routeProps }) => {
  const { checkGate, statsigUser } = useStatsig();
  const user = useSelector((state) => state.auth.user);
  const isEnabled = checkGate(gate);

  const [waitedOut, setWaitedOut] = useState(false);

  // Until redux auth hydrates, Statsig is asked about {userID: null, email: null}:
  // every user- and email-targeted rule misses, so the viewer falls through to the
  // gate's catch-all and an explicitly-excluded viewer is hopped to the new app
  // before anyone knew who they were. "Not resolved yet" is not "anonymous".
  // `document` is guarded because SSR has no cookie jar and no session to wait for.
  const hasSession = typeof document !== 'undefined' && Boolean(Cookies.get('jwt'));
  const statsigHasUser =
    statsigUser?.userID != null && String(statsigUser.userID) === String(user?.id ?? '');
  const identityReached = !hasSession || statsigHasUser;
  const isIdentityResolved = identityReached || waitedOut;

  // Timing out with a redux user in hand means Statsig never caught up on an
  // identity we know exists, so the gate answer is still the anonymous one and
  // acting on it is the exact harm this component exists to prevent — fail safe
  // to the legacy page instead. With no redux user the cookie is stale: no
  // identity is coming, and the anonymous answer is the correct one.
  const failSafeToLegacy = waitedOut && !identityReached && user?.id != null;
  const shouldRedirect = isIdentityResolved && !failSafeToLegacy && isEnabled;

  useEffect(() => {
    if (isIdentityResolved) {
      return undefined;
    }
    const timer = setTimeout(() => setWaitedOut(true), IDENTITY_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isIdentityResolved]);

  useEffect(() => {
    if (shouldRedirect && enabledTo) {
      // Always external. The query string must survive the hop — Stripe
      // Connect's OAuth return lands on /:clubType/:clubId/payment?code=...
      // and the new app completes the exchange from that param.
      const { search = '' } = window.location;
      let target = enabledTo;
      if (search) {
        target = `${enabledTo}${enabledTo.includes('?') ? '&' : '?'}${search.slice(1)}`;
      }
      window.location.href = target;
    }
  }, [shouldRedirect, enabledTo]);

  // Rendering the legacy page while identity resolves would flash it for a
  // viewer who is about to be redirected away — but a blank screen for up to
  // IDENTITY_WAIT_MS is its own bug, so show the same spinner these routes
  // already use as their lazy-chunk Suspense fallback.
  if (!isIdentityResolved || shouldRedirect) {
    return <RouteLoading />;
  }

  // FF OFF → render legacy component
  return <Component {...routeProps} />;
};

export default StatsigRouteSwitch;
